import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import API from "config/ApiConfig";
import { Mosaic } from "react-loading-indicators";

// constants
const API_BASE_URL = API.API_ADMIN;
function getToken() {
  return localStorage.getItem('authToken');
}

// Fungsi untuk mendapatkan header dengan token JWT
function getAuthHeaders() {
  const token = getToken();
  if (!token) {
      throw new Error('No token available');
  }
  return {
      'Authorization': `Bearer ${token}`, // Format token JWT
      'Content-Type': 'application/json' // Jika perlu, untuk menentukan jenis konten
  };
}

export default function CardTable({ color }) {
  const [dataSiswa, setDataSiswa] = useState([]);
  const [tentors, setTentors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const handleDropdownToggle = (_id) => {
    setOpenDropdownId(openDropdownId === _id ? null : _id);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const siswaResponse = await fetch(`${API_BASE_URL}/siswa`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (!siswaResponse.ok) {
          throw new Error(`HTTP error! Status: ${siswaResponse.status}`);
        }
        const siswaData = await siswaResponse.json();

        // Get unique tentor IDs from siswa data
        const tentorIds = [
          ...new Set(
            siswaData.flatMap(item => Array.isArray(item.tentor_id) ? item.tentor_id : [item.tentor_id])
          )
        ];

        const tentorRequests = tentorIds.map(id => 
          fetch(`${API_BASE_URL}/tentor/${id}`, {
            method: 'GET',
            headers: getAuthHeaders()
          }).then(response => {
            if (response.ok) {
              return response.json();
            } else {
              return response.text().then(text => {
                console.error(`Error fetching tentor ${id}: ${text}`);
                return null; // Return null for error cases
              });
            }
          })
        );

        const tentorData = await Promise.all(tentorRequests);
        const tentorsMap = tentorData.reduce((acc, tentor) => {
          if (tentor && tentor._id) {
            acc[tentor._id] = tentor;
          }
          return acc;
        }, {});

        setDataSiswa(siswaData);
        setTentors(tentorsMap);
      } catch (error) {
        setError(error.message);
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="flex m-4 justify-center items-center w-screen"><Mosaic color="#1177bc" size="large" text="Loading.." textColor="" /></div>
  }
  if (error) return <div>Error: {error}</div>;

  // Filter data based on the search query
  const filteredData = dataSiswa.filter(siswa => {
    const tentorNames = siswa.tentor_id 
      ? siswa.tentor_id
          .map(id => tentors[id]?.nama || '')
          .filter(name => name !== '')
          .join(', ')
      : '';
    
    return (
      siswa.nama.toLowerCase().includes(searchQuery.toLowerCase()) ||
      siswa.nama_sekolah.toLowerCase().includes(searchQuery.toLowerCase()) ||
      siswa.alamat.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tentorNames.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <>
      <div className="flex flex-wrap mt-4 mb-4">
        <a href="/admin/siswa/create">
          <button className="bg-lightBlue-500 text-white active:bg-lightBlue-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150">
            <i className="fas fa-plus"></i>
          </button>
        </a>
        <input 
          type="text" 
          className="px-2 py-1 w-4 rounded-lg shadow space-x-2" 
          placeholder="Cari"
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>
      <div
        className={
          "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3
                className={
                  "font-semibold text-lg " +
                  (color === "light" ? "text-blueGray-700" : "text-white")
                }
              >
                Siswa Table
              </h3>
            </div>
          </div>
        </div>
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                {["Nama", "Telepon", "Alamat", "Sekolah", "Kelas", "Daftar", "Tentor"].map((heading, index) => (
                  <th
                    key={index}
                    className={
                      "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                      (color === "light"
                        ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                        : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                    }
                  >
                    {heading}
                  </th>
                ))}
                <th
                  className={
                    "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                ></th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item._id}>
                  <th className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left flex items-center">
                    <span
                      className={
                        "ml-3 font-bold " +
                        (color === "light" ? "text-blueGray-600" : "text-white")
                      }
                    >
                      {item.nama}
                    </span>
                  </th>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    {item.telepon}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    {item.alamat}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                     {item.nama_sekolah}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                     {item.kelas}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    {item.createdAt}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    {/* Display tentors with comma separation */}
                    {item.tentor_id 
                      ? item.tentor_id
                          .map(id => tentors[id]?.nama || '')
                          .filter(name => name !== '')
                          .join(', ')
                      : 'Belum ada'}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right">
                    <div className="relative inline-block text-left">
                      <div>
                        <button
                          type="button"
                          className="flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                          onClick={() => handleDropdownToggle(item._id)}
                        >
                          <i className="fas fa-ellipsis-v"></i>
                        </button>
                      </div>

                      {openDropdownId === item._id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded shadow-lg z-10">
                           <a
                            href={`/admin/siswa/view/${item._id}`}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <i className="fas fa-eye mr-2"></i> View
                          </a>
                          <a
                            href={`/admin/siswa/update/${item._id}`}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <i className="fas fa-pen mr-2"></i> Update
                          </a>
                          <a
                            href={`/admin/siswa/delete/${item._id}`}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <i className="fas fa-trash mr-2"></i> Delete
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

CardTable.defaultProps = {
  color: "light",
};

CardTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
};
